/**
 * Vocabulaire metier de la plateforme DB.
 *
 * Ce fichier est la source de verite cote TypeScript. Les memes valeurs seront
 * declarees en Phase 2 sous forme de types ENUM PostgreSQL, afin que la base de
 * donnees refuse toute valeur inconnue (voir docs/DATABASE.md).
 */

/* -------------------------------------------------------------------------- */
/* Roles                                                                       */
/* -------------------------------------------------------------------------- */

// `provider` couvre le garage mécanique ET le lavage auto : c'est le même
// besoin — être trouvé, montrer ses prestations, recevoir des rendez-vous. Le
// métier exercé est une propriété de la fiche, pas un rôle de plus.
export const USER_ROLES = ['customer', 'partner', 'seller', 'provider', 'admin'] as const;
export type UserRole = (typeof USER_ROLES)[number];

/** Type de partenaire : agence aujourd'hui, particulier en phase P2P. */
export const PARTNER_KINDS = ['agency', 'individual'] as const;
export type PartnerKind = (typeof PARTNER_KINDS)[number];

/** Rôle d'un utilisateur au sein d'une agence. */
export const MEMBER_ROLES = ['owner', 'staff'] as const;
export type MemberRole = (typeof MEMBER_ROLES)[number];

export const DOCUMENT_STATUSES = ['pending', 'approved', 'rejected'] as const;
export type DocumentStatus = (typeof DOCUMENT_STATUSES)[number];

export const DOCUMENT_STATUS_LABELS: Record<DocumentStatus, string> = {
  pending: 'En attente de vérification',
  approved: 'Validé',
  rejected: 'Refusé',
};

export const LOCATION_KINDS = ['agency', 'airport', 'hotel', 'home', 'custom'] as const;
export type LocationKind = (typeof LOCATION_KINDS)[number];

export const OPTION_PRICINGS = ['flat', 'per_day'] as const;
export type OptionPricing = (typeof OPTION_PRICINGS)[number];

export const PAYOUT_STATUSES = ['pending', 'processing', 'paid', 'failed'] as const;
export type PayoutStatus = (typeof PAYOUT_STATUSES)[number];

/* -------------------------------------------------------------------------- */
/* Cycle de vie d'une reservation                                              */
/* -------------------------------------------------------------------------- */

export const BOOKING_STATUSES = [
  'PENDING', // creee, en attente d'acceptation par l'agence
  'CONFIRMED', // acceptee par l'agence, en attente de paiement
  'PAID', // acompte ou total encaisse
  'READY', // vehicule prepare, etat des lieux de depart fait
  'IN_PROGRESS', // vehicule remis au client
  'COMPLETED', // vehicule restitue, etat des lieux de retour fait
  'CANCELLED', // annulee par le client ou l'admin
  'REFUSED', // refusee par l'agence
  'DISPUTED', // litige ouvert
] as const;
export type BookingStatus = (typeof BOOKING_STATUSES)[number];

/**
 * Transitions autorisees. Cette table est dupliquee en Phase 2 dans une fonction
 * PostgreSQL : le frontend s'en sert pour afficher/masquer les boutons, la base
 * s'en sert pour REFUSER toute transition illegale. Le frontend n'est jamais
 * l'autorite.
 */
export const BOOKING_TRANSITIONS: Record<BookingStatus, readonly BookingStatus[]> = {
  PENDING: ['CONFIRMED', 'REFUSED', 'CANCELLED'],
  CONFIRMED: ['PAID', 'CANCELLED', 'REFUSED'],
  PAID: ['READY', 'CANCELLED', 'DISPUTED'],
  READY: ['IN_PROGRESS', 'CANCELLED', 'DISPUTED'],
  IN_PROGRESS: ['COMPLETED', 'DISPUTED'],
  COMPLETED: ['DISPUTED'],
  CANCELLED: [],
  REFUSED: [],
  DISPUTED: ['COMPLETED', 'CANCELLED'],
};

export function canTransition(from: BookingStatus, to: BookingStatus): boolean {
  return BOOKING_TRANSITIONS[from].includes(to);
}

/** Libelles et couleurs d'affichage, en francais. */
export const BOOKING_STATUS_LABELS: Record<BookingStatus, string> = {
  PENDING: 'En attente',
  CONFIRMED: 'Confirmée',
  PAID: 'Payée',
  READY: 'Prête',
  IN_PROGRESS: 'En cours',
  COMPLETED: 'Terminée',
  CANCELLED: 'Annulée',
  REFUSED: 'Refusée',
  DISPUTED: 'Litige',
};

/* -------------------------------------------------------------------------- */
/* Paiements                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Moyens de paiement.
 *
 * Seules les especes sont acceptees : la base le fait respecter par une
 * contrainte, pas seulement l'interface. Les autres valeurs restent dans le
 * type de la base de donnees — PostgreSQL ne sait pas retirer une valeur d'une
 * enumeration — mais aucune ligne ne peut plus les porter.
 */
export const PAYMENT_PROVIDERS = ['cash'] as const;
export type PaymentProvider = (typeof PAYMENT_PROVIDERS)[number];

export const PAYMENT_PROVIDER_LABELS: Record<string, string> = {
  cash: 'Espèces',
};

export const PAYMENT_STATUSES = ['PENDING', 'SUCCESS', 'FAILED', 'REFUNDED'] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

export const PAYMENT_PURPOSES = ['deposit_down_payment', 'balance', 'full', 'security_deposit'] as const;
export type PaymentPurpose = (typeof PAYMENT_PURPOSES)[number];

/* -------------------------------------------------------------------------- */
/* Caution                                                                     */
/* -------------------------------------------------------------------------- */

export const DEPOSIT_STATUSES = [
  'PENDING',
  'RECEIVED',
  'HELD',
  'RELEASED',
  'PARTIAL_REFUND',
  'WITHHELD',
] as const;
export type DepositStatus = (typeof DEPOSIT_STATUSES)[number];

export const DEPOSIT_STATUS_LABELS: Record<DepositStatus, string> = {
  PENDING: 'Caution en attente',
  RECEIVED: 'Caution reçue',
  HELD: 'Caution bloquée',
  RELEASED: 'Caution restituée',
  PARTIAL_REFUND: 'Retenue partielle',
  WITHHELD: 'Caution retenue',
};

/* -------------------------------------------------------------------------- */
/* Vehicules                                                                   */
/* -------------------------------------------------------------------------- */

export const TRANSMISSIONS = ['manual', 'automatic'] as const;
export type Transmission = (typeof TRANSMISSIONS)[number];

export const FUEL_TYPES = ['petrol', 'diesel', 'hybrid', 'electric'] as const;
export type FuelType = (typeof FUEL_TYPES)[number];

export const VEHICLE_STATUSES = ['draft', 'pending_review', 'published', 'suspended', 'archived'] as const;
export type VehicleStatus = (typeof VEHICLE_STATUSES)[number];

/* -------------------------------------------------------------------------- */
/* Partenaires                                                                 */
/* -------------------------------------------------------------------------- */

export const PARTNER_STATUSES = ['pending', 'verified', 'suspended', 'rejected'] as const;
export type PartnerStatus = (typeof PARTNER_STATUSES)[number];

/* -------------------------------------------------------------------------- */
/* Inspections (etat des lieux)                                                */
/* -------------------------------------------------------------------------- */

export const INSPECTION_PHASES = ['departure', 'return'] as const;
export type InspectionPhase = (typeof INSPECTION_PHASES)[number];

export const INSPECTION_SLOTS = [
  'front',
  'rear',
  'left_side',
  'right_side',
  'interior',
  'dashboard',
  'odometer',
  'fuel_gauge',
  'damage',
] as const;
export type InspectionSlot = (typeof INSPECTION_SLOTS)[number];

export const INSPECTION_SLOT_LABELS: Record<InspectionSlot, string> = {
  front: 'Avant',
  rear: 'Arrière',
  left_side: 'Côté gauche',
  right_side: 'Côté droit',
  interior: 'Intérieur',
  dashboard: 'Tableau de bord',
  odometer: 'Kilométrage',
  fuel_gauge: 'Niveau de carburant',
  damage: 'Dommage constaté',
};

/* -------------------------------------------------------------------------- */
/* Litiges                                                                     */
/* -------------------------------------------------------------------------- */

export const DISPUTE_TYPES = [
  'damage',
  'deposit',
  'cancellation',
  'late_return',
  'vehicle_issue',
  'payment_issue',
  'other',
] as const;
export type DisputeType = (typeof DISPUTE_TYPES)[number];

export const DISPUTE_STATUSES = ['open', 'investigating', 'resolved', 'rejected'] as const;
export type DisputeStatus = (typeof DISPUTE_STATUSES)[number];


/** Etats d'une annonce de vente, du brouillon a la vente conclue. */
export const LISTING_STATUSES = [
  'draft', 'pending', 'published', 'refused', 'sold', 'archived',
] as const;
export type ListingStatus = (typeof LISTING_STATUSES)[number];

export const LISTING_STATUS_LABELS: Record<ListingStatus, string> = {
  draft: 'Brouillon',
  pending: 'En attente de validation',
  published: 'En ligne',
  refused: 'Refusée',
  sold: 'Vendue',
  archived: 'Retirée',
};

export type ListingCondition = 'new' | 'used';
export const LISTING_CONDITION_LABELS: Record<ListingCondition, string> = {
  new: 'Neuve',
  used: "D'occasion",
};
